import { useCallback, useEffect, useState } from 'react'
import { digitsOnly, isValidCpf } from '@/utils/cpf'
import type { CaptchaConfig } from '@/components/AleloCaptcha'
import { toTransactionErrorCode, type TransactionErrorLike } from '@/utils/transactionError'

type ScreenTexts = {
  title?: string
  usernamePlaceholder?: string
  buttonText?: string
  forgotPasswordText?: string
}

type LoginIdSdk = {
  login: (data: { username: string; captcha?: string }) => Promise<void>
  registerPasskeyAutofill?: (inputId?: string) => Promise<void>
  getErrors?: () => TransactionErrorLike[]
  screen?: {
    texts?: ScreenTexts
    resetPasswordLink?: string | null
    signupLink?: string | null
    isCaptchaAvailable?: boolean
    captcha?: CaptchaConfig | null
    captchaProvider?: string | null
    captchaSiteKey?: string | null
    captchaImage?: string | null
  }
  transaction?: {
    isForgotPasswordEnabled?: boolean
    isSignupEnabled?: boolean
    isPasskeyEnabled?: boolean
    showPasskeyAutofill?: boolean
  }
}

async function createLoginIdSdk(): Promise<LoginIdSdk | null> {
  try {
    const mod = await import('@auth0/auth0-acul-js/login-id')
    return new mod.default() as unknown as LoginIdSdk
  } catch {
    return null
  }
}

function resolveCaptcha(sdk: LoginIdSdk | null): CaptchaConfig | null {
  if (!sdk?.screen) return null
  const fromObj = sdk.screen.captcha
  if (fromObj?.provider) {
    return {
      provider: fromObj.provider,
      siteKey: fromObj.siteKey ?? sdk.screen.captchaSiteKey,
      image: fromObj.image ?? sdk.screen.captchaImage,
    }
  }
  if (sdk.screen.isCaptchaAvailable || sdk.screen.captchaProvider) {
    return {
      provider: sdk.screen.captchaProvider,
      siteKey: sdk.screen.captchaSiteKey,
      image: sdk.screen.captchaImage,
    }
  }
  return null
}

/**
 * Identifier First — CPF; Auth0 decide senha vs passkey.
 * Captcha obrigatório quando Bot Detection / Auth Challenge está ativo.
 */
export function useLoginIdManager() {
  const [error, setError] = useState<string>()
  const [sdk, setSdk] = useState<LoginIdSdk | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string>()

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const instance = await createLoginIdSdk()
      if (cancelled || !instance) return
      setSdk(instance)
      try {
        await instance.registerPasskeyAutofill?.('cpf')
      } catch (err) {
        console.warn('[login-id] passkey autofill não disponível', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const captchaConfig = resolveCaptcha(sdk)
  const captchaRequired = Boolean(captchaConfig?.provider)

  const onCaptchaToken = useCallback((token: string | undefined) => {
    setCaptchaToken(token)
  }, [])

  async function handleContinue(usernameRaw: string) {
    setError(undefined)
    const username = digitsOnly(usernameRaw)
    if (!isValidCpf(username)) {
      setError('CPF_INVALID')
      throw new Error('CPF_INVALID')
    }

    if (captchaRequired && !captchaToken) {
      setError('CAPTCHA_REQUIRED')
      throw new Error('CAPTCHA_REQUIRED')
    }

    const instance = sdk ?? (await createLoginIdSdk())
    if (instance?.login) {
      await instance.login({
        username,
        ...(captchaToken ? { captcha: captchaToken } : {}),
      })
      return
    }

    console.info('[preview login-id]', { username, captcha: Boolean(captchaToken) })
    window.dispatchEvent(
      new CustomEvent('alelo:acul-preview', {
        detail: { screen: 'login-id', action: 'login', data: { username } },
      }),
    )
  }

  async function handleForgotPassword() {
    const instance = sdk ?? (await createLoginIdSdk())
    const link = instance?.screen?.resetPasswordLink
    if (link) {
      window.location.assign(link)
      return
    }
    window.location.search = '?screen=reset-password-request'
  }

  function handleSignup() {
    const link = sdk?.screen?.signupLink
    if (link) {
      window.location.assign(link)
      return
    }
    // Identifier First costuma ir para signup-id; fallback single-step
    window.location.search = '?screen=signup-id'
  }

  // Falha no Continuar (ex.: captcha recusado) volta no re-render do Auth0.
  const serverError = toTransactionErrorCode(sdk?.getErrors?.())

  return {
    handleContinue,
    handleForgotPassword,
    handleSignup,
    captchaConfig,
    captchaRequired,
    captchaReady: !captchaRequired || Boolean(captchaToken),
    onCaptchaToken,
    texts: sdk?.screen?.texts,
    // Com múltiplas conexões habilitadas, a transação não fixa conexão e o
    // SDK devolve isForgotPasswordEnabled=false mesmo quando o Auth0 expõe o
    // link de reset — mostrar pelo link do contexto.
    showForgot:
      sdk?.transaction?.isForgotPasswordEnabled === true ||
      Boolean(sdk?.screen?.resetPasswordLink) ||
      sdk?.transaction?.isForgotPasswordEnabled === undefined,
    showSignup: sdk?.transaction?.isSignupEnabled !== false,
    error: error || serverError,
  }
}
